// src/App/WebSocketProvider.tsx
import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { WebSocketClient } from '../clients/websocketClient';
import { useAuth } from './AuthProvider';
import { WebSocketResponse } from '../schemas/webSocketResponseSchema';
import { WEBSOCKET_MESSAGE_TYPES } from '../constants/webSocketTypes';
import { useToast } from '../hooks/useToast';
import { useTranslation } from 'react-i18next';

interface WebSocketContextProps {
    wsClient: WebSocketClient | null;
    sendMessage: (msg: WebSocketResponse<any>) => void;
    onMessage: (callback: (msg: WebSocketResponse<any>) => void) => void;
    offMessage: (callback: (msg: WebSocketResponse<any>) => void) => void;
    status: 'connected' | 'disconnected' | 'connecting';
}


const WebSocketContext = createContext<WebSocketContextProps>({
    wsClient: null,
    sendMessage: () => { },
    onMessage: () => { },
    offMessage: () => { },
    status: 'disconnected',
});

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider: React.FC<{ children: React.ReactNode; wsUrl: string; }> = ({ children, wsUrl }) => {
    const wsClientRef = useRef<WebSocketClient | null>(null);
    const [status, setStatus] = React.useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
    const { isAuthenticated } = useAuth();
    const { showSuccess, showWarning, showError, showInfo } = useToast();
    const { t } = useTranslation();

    useEffect(() => {
        if (!isAuthenticated) {
            wsClientRef.current?.close();
            setStatus('disconnected');
            // Clean up global reference
            if ((window as any).wsClient) {
                delete (window as any).wsClient;
            }
            return;
        }
        wsClientRef.current = new WebSocketClient(wsUrl);
        wsClientRef.current.onStatus(setStatus);

        // Setup auto-toast on message received
        wsClientRef.current.onMessage((msg: WebSocketResponse<any>) => {
            if (msg.status !== 200) {
                const msgData = msg.data as any;
                if (msg.status === 403 && msgData?.error === 'fiscal_date_not_configured') {
                    showError(
                        t('page.mobile_pos_update.drawer.toast.fiscal_date_not_configured.title'),
                        t('page.mobile_pos_update.drawer.toast.fiscal_date_not_configured.message')
                    );
                    // Dispatch custom event so UI can handle failed requests without refreshing
                    window.dispatchEvent(new CustomEvent('mobile-pos-transaction-completed', { detail: msg.data }));
                } else {
                    showError(
                        t('page.websocket.error_on', { defaultValue: '🚫 Error on: {{type}}', type: msg.type }),
                        msg.message || t('page.websocket.error_default', { defaultValue: 'An error occurred while processing, please contact support team' })
                    );
                }
                return;
            }

            // eslint-disable-next-line
            console.log('WebSocket message received: ', msg);

            // Check if this is a report progress update
            const msgData = msg as any;
            if (msgData.report_id && msgData.percentage !== undefined && msgData.step !== undefined) {
                // Dispatch as custom event for useReportProgress hook
                const progressEvent = new CustomEvent('report-progress', {
                    detail: msgData
                });
                window.dispatchEvent(progressEvent);
                return; // Don't show toast for progress updates
            }

            const type = msg.type;

            // Show toast based on message type
            switch (type) {
                case WEBSOCKET_MESSAGE_TYPES.PING:
                case WEBSOCKET_MESSAGE_TYPES.PONG:
                    // Ignore ping/pong
                    break;

                case WEBSOCKET_MESSAGE_TYPES.NOTIFICATION:
                    showInfo(
                        t('page.websocket.notification.title', { defaultValue: 'Notification' }),
                        msg.message || t('page.websocket.notification.default', { defaultValue: 'New notification received' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.SYSTEM_UPDATE:
                    showInfo(
                        t('page.websocket.system_update.title', { defaultValue: 'System Update' }),
                        msg.message || t('page.websocket.system_update.default', { defaultValue: 'System update received' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAYMENT_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_payment.completed.title', { defaultValue: 'Transaction Payment' }),
                        msg.message || t('page.websocket.transaction_payment.completed.default', { defaultValue: 'Payment transaction processed successfully' })
                    );
                    window.dispatchEvent(new CustomEvent('transaction-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAYMENT_APPROVED:
                    showInfo(
                        t('page.websocket.transaction_payment.approved.title', { defaultValue: 'Transaction Payment Approved' }),
                        msg.message || t('page.websocket.transaction_payment.approved.default', { defaultValue: 'Payment transaction has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAYMENT_REJECTED:
                    showWarning(
                        t('page.websocket.transaction_payment.rejected.title', { defaultValue: 'Transaction Payment Rejected' }),
                        msg.message || t('page.websocket.transaction_payment.rejected.default', { defaultValue: 'Payment transaction has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_WITHDRAWAL_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_withdrawal.completed.title', { defaultValue: 'Transaction Withdrawal' }),
                        msg.message || t('page.websocket.transaction_withdrawal.completed.default', { defaultValue: 'Withdrawal transaction processed successfully' })
                    );
                    window.dispatchEvent(new CustomEvent('transaction-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_WITHDRAWAL_APPROVED:
                    showInfo(
                        t('page.websocket.transaction_withdrawal.approved.title', { defaultValue: 'Transaction Withdrawal Approved' }),
                        msg.message || t('page.websocket.transaction_withdrawal.approved.default', { defaultValue: 'Withdrawal transaction has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_WITHDRAWAL_REJECTED:
                    showWarning(
                        t('page.websocket.transaction_withdrawal.rejected.title', { defaultValue: 'Transaction Withdrawal Rejected' }),
                        msg.message || t('page.websocket.transaction_withdrawal.rejected.default', { defaultValue: 'Withdrawal transaction has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_RECEIPT_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_receipt.completed.title', { defaultValue: 'Transaction Receipt' }),
                        msg.message || t('page.websocket.transaction_receipt.completed.default', { defaultValue: 'Receipt transaction processed successfully' })
                    );
                    window.dispatchEvent(new CustomEvent('transaction-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_RECEIPT_APPROVED:
                    showInfo(
                        t('page.websocket.transaction_receipt.approved.title', { defaultValue: 'Transaction Receipt Approved' }),
                        msg.message || t('page.websocket.transaction_receipt.approved.default', { defaultValue: 'Receipt transaction has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_RECEIPT_REJECTED:
                    showWarning(
                        t('page.websocket.transaction_receipt.rejected.title', { defaultValue: 'Transaction Receipt Rejected' }),
                        msg.message || t('page.websocket.transaction_receipt.rejected.default', { defaultValue: 'Receipt transaction has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_JOURNAL_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_journal.completed.title', { defaultValue: 'Transaction Journal' }),
                        msg.message || t('page.websocket.transaction_journal.completed.default', { defaultValue: 'Journal transaction processed successfully' })
                    );
                    window.dispatchEvent(new CustomEvent('transaction-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_JOURNAL_APPROVED:
                    showInfo(
                        t('page.websocket.transaction_journal.approved.title', { defaultValue: 'Transaction Journal Approved' }),
                        msg.message || t('page.websocket.transaction_journal.approved.default', { defaultValue: 'Journal transaction has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_JOURNAL_REJECTED:
                    showWarning(
                        t('page.websocket.transaction_journal.rejected.title', { defaultValue: 'Transaction Journal Rejected' }),
                        msg.message || t('page.websocket.transaction_journal.rejected.default', { defaultValue: 'Journal transaction has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_INVOICE_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_invoice.completed.title', { defaultValue: 'Invoice Transaction' }),
                        msg.message || t('page.websocket.transaction_invoice.completed.default', { defaultValue: 'Invoice transaction processed successfully' })
                    );
                    // Dispatch custom event so Invoice page can refresh or react
                    window.dispatchEvent(new CustomEvent('transaction-invoice-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PURCHASE_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_purchase.completed.title', { defaultValue: 'Purchase Transaction' }),
                        msg.message || t('page.websocket.transaction_purchase.completed.default', { defaultValue: 'Purchase transaction processed successfully' })
                    );
                    // Dispatch custom event so Purchasing page can refresh or react
                    window.dispatchEvent(new CustomEvent('transaction-purchase-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_INVOICE_APPROVED:
                    showInfo(
                        t('page.websocket.transaction_invoice.approved.title', { defaultValue: 'Invoice Transaction Approved' }),
                        msg.message || t('page.websocket.transaction_invoice.approved.default', { defaultValue: 'Invoice transaction has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_INVOICE_REJECTED:
                    showWarning(
                        t('page.websocket.transaction_invoice.rejected.title', { defaultValue: 'Invoice Transaction Rejected' }),
                        msg.message || t('page.websocket.transaction_invoice.rejected.default', { defaultValue: 'Invoice transaction has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_CANCELLATION_COMPLETED:
                    showSuccess(
                        t('page.websocket.transaction_cancellation.completed.title', { defaultValue: 'Transaction Cancelled' }),
                        msg.message || t('page.websocket.transaction_cancellation.completed.default', { defaultValue: 'Transaction has been cancelled successfully' })
                    );
                    // Dispatch custom event for CancelTransaction page to refresh
                    window.dispatchEvent(new CustomEvent('transaction-cancellation-completed', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_APPROVAL_PENDING:
                    showWarning(
                        t('page.websocket.transaction_approval.pending.title', { defaultValue: 'Transaction Approval' }),
                        msg.message || t('page.websocket.transaction_approval.pending.default', { defaultValue: 'Transaction awaiting approval' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_MOBILE_POS_COMPLETED:
                    showSuccess(
                        t('page.mobile_pos_update.drawer.toast.confirm_success.title'),
                        msg.message || t('page.mobile_pos_update.drawer.toast.confirm_success.message')
                    );
                    // Dispatch custom event for MobilePOSUpdate page to refresh
                    window.dispatchEvent(new CustomEvent('mobile-pos-transaction-completed', { detail: msg.data }));
                    break;
                case WEBSOCKET_MESSAGE_TYPES.PROCESS_DAY_END_PROCESSING:
                    showInfo(
                        t('page.websocket.day_end.processing.title', { defaultValue: 'Day End Process' }),
                        msg.message || t('page.websocket.day_end.processing.default', { defaultValue: 'Day end process is in progress' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.PROCESS_DAY_END_COMPLETED:
                    showSuccess(
                        t('page.websocket.day_end.completed.title', { defaultValue: 'Day End Process Completed' }),
                        msg.message || t('page.websocket.day_end.completed.default', { defaultValue: 'Day end process has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.PROCESS_MONTH_END_PROCESSING:
                    showInfo(
                        t('page.websocket.month_end.processing.title', { defaultValue: 'Month End Process' }),
                        msg.message || t('page.websocket.month_end.processing.default', { defaultValue: 'Month end process is in progress' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.PROCESS_MONTH_END_COMPLETED:
                    showSuccess(
                        t('page.websocket.month_end.completed.title', { defaultValue: 'Month End Process Completed' }),
                        msg.message || t('page.websocket.month_end.completed.default', { defaultValue: 'Month end process has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.PROCESS_YEAR_END_PROCESSING:
                    showInfo(
                        t('page.websocket.year_end.processing.title', { defaultValue: 'Year End Process' }),
                        msg.message || t('page.websocket.year_end.processing.default', { defaultValue: 'Year end process is in progress' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.PROCESS_YEAR_END_COMPLETED:
                    showSuccess(
                        t('page.websocket.year_end.completed.title', { defaultValue: 'Year End Process Completed' }),
                        msg.message || t('page.websocket.year_end.completed.default', { defaultValue: 'Year end process has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.FD_ADVANCE_APPLICATION_SUBMITTED:
                    showSuccess(
                        t('page.websocket.fd_advance.submitted.title', { defaultValue: 'FD Advance Loan Submitted' }),
                        msg.message || t('page.websocket.fd_advance.submitted.default', { defaultValue: 'FD Advance loan application has been submitted successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.REPORT_PROGRESS:
                    // Dispatch custom event for report progress tracking
                    window.dispatchEvent(new CustomEvent('report-progress', { detail: msg.data }));
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_LOAN_DISBURSEMENT_APPROVED:
                    showInfo(
                        t('page.websocket.loan_disbursement.approved.title', { defaultValue: 'Loan Disbursement Approved' }),
                        msg.message || t('page.websocket.loan_disbursement.approved.default', { defaultValue: 'Loan disbursement has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_LOAN_DISBURSEMENT_REJECTED:
                    showWarning(
                        t('page.websocket.loan_disbursement.rejected.title', { defaultValue: 'Loan Disbursement Rejected' }),
                        msg.message || t('page.websocket.loan_disbursement.rejected.default', { defaultValue: 'Loan disbursement has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_LOAN_DISBURSEMENT_COMPLETED:
                    showSuccess(
                        t('page.websocket.loan_disbursement.completed.title', { defaultValue: 'Loan Disbursement Completed' }),
                        msg.message || t('page.websocket.loan_disbursement.completed.default', { defaultValue: 'Loan disbursement has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_AUTO_RECOVERY_COMPLETED:
                    showSuccess(
                        t('page.websocket.auto_recovery.completed.title', { defaultValue: 'Auto Recovery Completed' }),
                        msg.message || t('page.websocket.auto_recovery.completed.default', { defaultValue: 'Auto recovery process has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_EQUIPMENT_APPROVED:
                    showInfo(
                        t('page.websocket.equipment.approved.title', { defaultValue: 'Equipment Transaction Approved' }),
                        msg.message || t('page.websocket.equipment.approved.default', { defaultValue: 'Equipment transaction has been approved' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_EQUIPMENT_REJECTED:
                    showWarning(
                        t('page.websocket.equipment.rejected.title', { defaultValue: 'Equipment Transaction Rejected' }),
                        msg.message || t('page.websocket.equipment.rejected.default', { defaultValue: 'Equipment transaction has been rejected' })
                    );
                    break;
                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_EQUIPMENT_COMPLETED:
                    showSuccess(
                        t('page.websocket.equipment.completed.title', { defaultValue: 'Equipment Transaction Completed' }),
                        msg.message || t('page.websocket.equipment.completed.default', { defaultValue: 'Equipment transaction has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_INVESTMENT_APPROVED:
                    showInfo(
                        t('page.websocket.investment.approved.title', { defaultValue: 'Investment Transaction Approved' }),
                        msg.message || t('page.websocket.investment.approved.default', { defaultValue: 'Investment transaction has been approved' })
                    );
                    break;
                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_INVESTMENT_REJECTED:
                    showWarning(
                        t('page.websocket.investment.rejected.title', { defaultValue: 'Investment Transaction Rejected' }),
                        msg.message || t('page.websocket.investment.rejected.default', { defaultValue: 'Investment transaction has been rejected' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_INVESTMENT_COMPLETED:
                    showSuccess(
                        t('page.websocket.investment.completed.title', { defaultValue: 'Investment Transaction Completed' }),
                        msg.message || t('page.websocket.investment.completed.default', { defaultValue: 'Investment transaction has been completed successfully' })
                    );
                    break;

                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAY_TO_CREDITOR_APPROVED:
                    showInfo(
                        t('page.websocket.pay_to_creditor.approved.title', { defaultValue: 'Pay to Creditor Transaction Approved' }),
                        msg.message || t('page.websocket.pay_to_creditor.approved.default', { defaultValue: 'Pay to Creditor transaction has been approved' })
                    );
                    break;
                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAY_TO_CREDITOR_REJECTED:
                    showWarning(
                        t('page.websocket.pay_to_creditor.rejected.title', { defaultValue: 'Pay to Creditor Transaction Rejected' }),
                        msg.message || t('page.websocket.pay_to_creditor.rejected.default', { defaultValue: 'Pay to Creditor transaction has been rejected' })
                    );
                    break;
                case WEBSOCKET_MESSAGE_TYPES.TRANSACTION_PAY_TO_CREDITOR_COMPLETED:
                    showSuccess(
                        t('page.websocket.pay_to_creditor.completed.title', { defaultValue: 'Pay to Creditor Transaction Completed' }),
                        msg.message || t('page.websocket.pay_to_creditor.completed.default', { defaultValue: 'Pay to Creditor transaction has been completed successfully' })
                    );
                    break;
                case WEBSOCKET_MESSAGE_TYPES.SYSTEM_LOG_CREATED:
                    break

                default:
                    // Show generic toast for unknown types
                    if (msg.message) {
                        showInfo(t('page.websocket.update.title', { defaultValue: 'Update' }), msg.message);
                    }
                    break;
            }
        });

        wsClientRef.current.connect();

        // Expose WebSocket client globally for worker communication
        (window as any).wsClient = wsClientRef.current;

        return () => {
            wsClientRef.current?.close();
            if ((window as any).wsClient) {
                delete (window as any).wsClient;
            }
        };
    }, [wsUrl, isAuthenticated, showSuccess, showWarning, showError, showInfo]);

    const sendMessage = useCallback((msg: WebSocketResponse<any>) => {
        wsClientRef.current?.send(msg);
    }, []);

    const onMessage = useCallback((callback: (msg: WebSocketResponse<any>) => void) => {
        wsClientRef.current?.onMessage(callback);
    }, []);

    const offMessage = useCallback((callback: (msg: WebSocketResponse<any>) => void) => {
        wsClientRef.current?.offMessage(callback);
    }, []);

    return (
        <WebSocketContext.Provider value={{ wsClient: wsClientRef.current, sendMessage, onMessage, offMessage, status }}>
            {children}
        </WebSocketContext.Provider>
    );
};
